'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Bell, Search, Menu, Wifi, WifiOff, ChevronDown, LogOut, User, Settings } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface TopbarProps {
  pageTitle?: string;
  pageSubtitle?: string;
  onMenuToggle?: () => void;
}

export default function Topbar({ pageTitle, pageSubtitle, onMenuToggle }: TopbarProps) {
  const [isOnline] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, signOut, getDisplayName, getInitials } = useAuth();
  const router = useRouter();

  const displayName = getDisplayName ? getDisplayName() : (user?.email?.split('@')[0] || 'User');
  const initials = getInitials ? getInitials() : displayName.slice(0, 2).toUpperCase();
  const shortName = displayName.split(' ')[0] + (displayName.split(' ')[1] ? ' ' + displayName.split(' ')[1][0] + '.' : '');

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    setDropdownOpen(false);
    try {
      await signOut();
      toast.success('Signed out successfully');
      router.push('/sign-up-login-screen');
    } catch {
      toast.error('Sign out failed. Please try again.');
    }
  };

  return (
    <header className="h-16 bg-card border-b border-border flex items-center px-4 sm:px-6 gap-4 flex-shrink-0 z-20">
      {/* Mobile logo */}
      <div className="flex items-center gap-2 lg:hidden">
        <button
          onClick={onMenuToggle}
          className="p-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
          aria-label="Toggle menu"
        >
          <Menu size={20} />
        </button>
        <AppLogo size={28} />
        <span className="font-bold text-base text-primary">MyDoQs</span>
      </div>

      {/* Page title — desktop */}
      <div className="hidden lg:block flex-1">
        {pageTitle && (
          <div>
            <h1 className="text-lg font-600 text-foreground">{pageTitle}</h1>
            {pageSubtitle && <p className="text-xs text-muted-foreground">{pageSubtitle}</p>}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 ml-auto">
        {/* Online/Offline indicator */}
        <div className={`hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-500 ${isOnline ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
          {isOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
          <span>{isOnline ? 'Online' : 'Offline'}</span>
        </div>

        {/* Search */}
        <button className="p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-primary transition-colors" aria-label="Search">
          <Search size={18} />
        </button>

        {/* Notifications */}
        <Link href="/notification-center" className="relative p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-primary transition-colors" aria-label="Notifications">
          <Bell size={18} />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full" />
        </Link>

        {/* User avatar with dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 pl-2 rounded-lg hover:bg-muted transition-colors py-1 pr-1"
            aria-label="User menu"
            aria-expanded={dropdownOpen}
          >
            <div className="w-8 h-8 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center text-primary font-600 text-sm">
              {initials}
            </div>
            <span className="hidden sm:block text-sm font-500 text-foreground max-w-[120px] truncate">{shortName}</span>
            <ChevronDown size={14} className="hidden sm:block text-muted-foreground" />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded-xl shadow-lg z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-600 text-primary truncate">{displayName}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <div className="py-1">
                <Link
                  href="/family-dashboard"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                >
                  <User size={15} className="text-muted-foreground" />
                  Profile & Family
                </Link>
                <Link
                  href="/settings"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                >
                  <Settings size={15} className="text-muted-foreground" />
                  Settings
                </Link>
              </div>
              <div className="border-t border-border py-1">
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-danger hover:bg-danger/10 transition-colors"
                >
                  <LogOut size={15} />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}